import { useCallback, useEffect, useRef, useState } from 'react'
import { loadYouTubeIframeApi, YT_STATE, type YouTubePlayerLike } from 'eyecontact'

export interface UseYouTubePlayerResult {
  containerRef: React.RefObject<HTMLDivElement | null>
  player: YouTubePlayerLike | null
  ready: boolean
  playerState: number
  error: string | null
  load: (videoId: string) => void
}

interface YTPlayerCtor {
  new (
    el: HTMLElement,
    config: {
      videoId?: string
      playerVars?: Record<string, unknown>
      events?: Record<string, (e: unknown) => void>
    },
  ): YouTubePlayerLike & { loadVideoById(id: string): void; destroy(): void }
}

/**
 * Mounts a YouTube IFrame player into a div and keeps its state in React.
 *
 * Two things worth knowing. First, the API replaces the element you hand it,
 * so we mount into a throwaway child rather than the ref itself, or React and
 * YouTube end up fighting over the same DOM node. Second, we never autoplay:
 * the first play has to come from the user or the browser blocks it anyway.
 */
export function useYouTubePlayer(initialVideoId?: string): UseYouTubePlayerResult {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const playerRef = useRef<(YouTubePlayerLike & { loadVideoById(id: string): void; destroy(): void }) | null>(null)
  const pendingId = useRef<string | undefined>(initialVideoId)

  const [player, setPlayer] = useState<YouTubePlayerLike | null>(null)
  const [ready, setReady] = useState(false)
  const [playerState, setPlayerState] = useState<number>(YT_STATE.UNSTARTED)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    loadYouTubeIframeApi()
      .then((yt) => {
        if (cancelled || !containerRef.current) return

        const mount = document.createElement('div')
        containerRef.current.innerHTML = ''
        containerRef.current.appendChild(mount)

        const Ctor = (yt as { Player: YTPlayerCtor }).Player
        const p = new Ctor(mount, {
          ...(pendingId.current ? { videoId: pendingId.current } : {}),
          playerVars: {
            rel: 0,
            modestbranding: 1,
            playsinline: 1,
            origin: window.location.origin,
          },
          events: {
            onReady: () => {
              if (cancelled) return
              setReady(true)
              setPlayer(p)
            },
            onStateChange: (e: unknown) => {
              const data = (e as { data?: number })?.data
              if (typeof data === 'number') setPlayerState(data)
            },
            onError: (e: unknown) => {
              const code = (e as { data?: number })?.data
              setError(
                code === 101 || code === 150
                  ? 'The owner of this video has disabled embedding. Try a different one.'
                  : `YouTube player error (code ${code ?? 'unknown'}).`,
              )
            },
          },
        })
        playerRef.current = p
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message)
      })

    return () => {
      cancelled = true
      try {
        playerRef.current?.destroy()
      } catch {
        /* the iframe may already be gone during a hot reload */
      }
      playerRef.current = null
    }
  }, [])

  const load = useCallback((videoId: string) => {
    pendingId.current = videoId
    setError(null)
    const p = playerRef.current
    if (p) {
      p.loadVideoById(videoId)
    }
  }, [])

  return { containerRef, player, ready, playerState, error, load }
}
